import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import {
  fetchPartner, fetchFoodLogs, fetchSchedules, createSchedule, updateSchedule,
  fetchBlackoutDays, createBlackoutDay, deleteBlackoutDay,
  fetchAddresses, fetchUsers,
} from "api";
import { PageHeader, Table, Spinner, EmptyState, FormField, Modal, Select } from "components/common";
import { useAuth } from "hooks/useAuth";
import { formatSlotTime, slotToInput, inputToSlot, SCHEDULE_TIME_ZONE_LABEL } from "utils/scheduleTime";
import type { FoodLog, Schedule, DayOfWeek, BlackoutDay, EntityAddress, ContactUser } from "types";

// ─── Partner info & schedules page ───────────────────────────────────────────

export function PartnerInfoPage() {
  const { partnerEntityId } = useAuth();
  const queryClient         = useQueryClient();
  const partnerId           = Number(partnerEntityId);

  const [editingSchedule,   setEditingSchedule]   = useState<Schedule | null>(null);
  const [showAddSchedule,   setShowAddSchedule]   = useState(false);
  const [showBlackoutModal, setShowBlackoutModal] = useState(false);
  const [newNote, setNewNote]                     = useState("");

  const { data: partner, isLoading } = useQuery({
    queryKey: ["partners", partnerId],
    queryFn:  () => fetchPartner(partnerId),
    enabled:  !!partnerId,
  });

  const { data: schedules = [] } = useQuery({
    queryKey: ["schedules", partnerId],
    queryFn:  () => fetchSchedules({ partner_entity_id: String(partnerId) }),
    enabled:  !!partnerId,
  });

  const { data: blackoutDays = [] } = useQuery({
    queryKey: ["blackoutDays", partnerId],
    queryFn:  () => fetchBlackoutDays({ partner_entity_id: partnerId }),
    enabled:  !!partnerId,
  });

  const { data: addresses = [] } = useQuery({
    queryKey: ["addresses", partnerId],
    queryFn:  () => fetchAddresses({ partner_entity_id: String(partnerId) }),
    enabled:  !!partnerId,
  });

  const { data: contactUsers = [] } = useQuery({
    queryKey: ["users", { partner_entity_id: partnerId }],
    queryFn:  () => fetchUsers({ partner_entity_id: partnerId }),
    enabled:  !!partnerId,
  });

  const removeBlackoutDay = useMutation({
    mutationFn: (id: number) => deleteBlackoutDay(id),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ["blackoutDays", partnerId] }),
  });

  if (isLoading) return <Spinner className="mt-20" />;
  if (!partner)  return <EmptyState message="Partner info not found." />;

  const DAYS: DayOfWeek[] = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-2xl space-y-6">
      <PageHeader title={partner.name} subtitle="Info & Schedules" />

      {/* ── Address & map ─────────────────────────────────────────────────── */}
      {addresses.length > 0 && (
        <Section title="Location">
          <div className="space-y-4">
            {addresses.map((a) => (
              <div key={a.id}>
                <p className="text-sm text-gray-800">{a.address}{a.address_2 ? `, ${a.address_2}` : ""}</p>
                <p className="text-sm text-gray-500">{a.city}, {a.state} {a.zip}</p>
                {a.coordinates?.lat && a.coordinates?.lng && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-gray-200">
                    <iframe
                      title={`Map of ${a.address}`}
                      width="100%"
                      height="200"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${a.coordinates.lng - 0.005},${a.coordinates.lat - 0.003},${a.coordinates.lng + 0.005},${a.coordinates.lat + 0.003}&layer=mapnik&marker=${a.coordinates.lat},${a.coordinates.lng}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Contact users ─────────────────────────────────────────────────── */}
      <Section title="Team">
        {contactUsers.length === 0 ? (
          <p className="text-sm text-gray-400">No contacts on record.</p>
        ) : (
          <div className="space-y-3">
            {contactUsers.filter((u) => u.active).map((u) => (
              <ContactUserRow key={u.id} user={u} />
            ))}
            {contactUsers.some((u) => !u.active) && (
              <div className="pt-1 border-t border-gray-100 space-y-3 mt-1">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Inactive</p>
                {contactUsers.filter((u) => !u.active).map((u) => (
                  <ContactUserRow key={u.id} user={u} inactive />
                ))}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ── Schedules ────────────────────────────────────────────────────── */}
      <Section
        title={
          partner.type === "donator"     ? "Pickup schedules" :
          partner.type === "distributor" ? "Delivery schedules" :
          "Schedules"
        }
        action={
          schedules.length === 0 ? (
            <button
              className="text-xs text-brand-600 hover:text-brand-700 transition-colors"
              onClick={() => setShowAddSchedule(true)}
            >
              + Add schedule
            </button>
          ) : undefined
        }
      >
        {schedules.length === 0 ? (
          <p className="text-sm text-gray-400">No schedules configured.</p>
        ) : (
          <div className="space-y-4">
            {schedules.map((s) => (
              <div key={s.id} className="bg-gray-50 rounded-lg px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Schedule #{s.id}
                  </p>
                  <button
                    className="text-xs text-brand-600 hover:text-brand-700 transition-colors"
                    onClick={() => setEditingSchedule(s)}
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-1 text-sm">
                  {DAYS.filter((d) => (s.schedule[d]?.length ?? 0) > 0).map((day) => (
                    <div key={day} className="flex gap-2">
                      <span className="capitalize text-gray-500 w-24 shrink-0">{day}</span>
                      <div className="space-y-0.5">
                        {s.schedule[day]!.map((slot, i) => (
                          <div key={i} className="text-gray-800">
                            {formatSlotTime(slot.start)} – {formatSlotTime(slot.end)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {DAYS.every((d) => (s.schedule[d]?.length ?? 0) === 0) && (
                    <p className="text-gray-400 text-xs">No time slots set.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── Blackout days ─────────────────────────────────────────────────── */}
      <Section
        title="Blackout days"
        action={
          <button
            className="text-xs text-brand-600 hover:text-brand-700 transition-colors"
            onClick={() => setShowBlackoutModal(true)}
          >
            + Add
          </button>
        }
      >
        {blackoutDays.length === 0 ? (
          <p className="text-sm text-gray-400">No blackout days yet.</p>
        ) : (
          <div className="space-y-1">
            {blackoutDays.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                <span className="text-sm text-gray-800">{format(parseISO(b.date), "EEEE, MMMM d, yyyy")}</span>
                <button
                  className="text-xs text-red-400 hover:text-red-600 transition-colors ml-4"
                  onClick={() => removeBlackoutDay.mutate(b.id)}
                  disabled={removeBlackoutDay.isPending}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      {editingSchedule && (
        <ScheduleModal
          partnerId={partnerId}
          addresses={addresses}
          existing={editingSchedule}
          onClose={() => setEditingSchedule(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["schedules", partnerId] });
            setEditingSchedule(null);
          }}
        />
      )}

      {showAddSchedule && (
        <ScheduleModal
          partnerId={partnerId}
          addresses={addresses}
          existing={null}
          onClose={() => setShowAddSchedule(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["schedules", partnerId] });
            setShowAddSchedule(false);
          }}
        />
      )}

      {showBlackoutModal && (
        <AddBlackoutDayModal
          partnerId={partnerId}
          existingDates={blackoutDays.map((b) => b.date)}
          onClose={() => setShowBlackoutModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["blackoutDays", partnerId] });
            setShowBlackoutModal(false);
          }}
        />
      )}
    </div>
  );
}

// ─── Partner history page ─────────────────────────────────────────────────────

const MONTHS_PER_PAGE = 3;

export function PartnerHistoryPage() {
  const { partnerEntityId } = useAuth();
  const navigate            = useNavigate();
  const partnerId           = Number(partnerEntityId);
  const [page, setPage]     = useState(0);

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["foodLogs", partnerId],
    queryFn:  () => fetchFoodLogs({ partner_entity_id: String(partnerId) }),
    enabled:  !!partnerId,
  });

  const grouped = logs.reduce<Record<string, FoodLog[]>>((acc, log) => {
    const key = format(parseISO(log.created_at), "MMMM yyyy");
    if (!acc[key]) acc[key] = [];
    acc[key].push(log);
    return acc;
  }, {});

  const monthKeys   = Object.keys(grouped);
  const totalPages  = Math.ceil(monthKeys.length / MONTHS_PER_PAGE);
  const visibleKeys = monthKeys.slice(page * MONTHS_PER_PAGE, (page + 1) * MONTHS_PER_PAGE);

  if (isLoading) return <Spinner className="mt-20" />;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      <PageHeader title="Pickup history" subtitle="All food log records" />

      {monthKeys.length === 0 ? (
        <EmptyState message="No food logs yet." />
      ) : (
        <div className="space-y-8">
          {visibleKeys.map((month) => (
            <div key={month}>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {month}
              </h2>
              <div className="card">
                <Table
                  data={grouped[month]}
                  keyExtractor={(l) => l.id}
                  onRowClick={(l) => navigate(`/history/${l.id}`)}
                  columns={[
                    {
                      header:   "Date",
                      accessor: (l) => format(parseISO(l.created_at), "MMM d"),
                    },
                    {
                      header:   "Poundage",
                      accessor: (l) => `${l.poundage} lbs`,
                    },
                    {
                      header:   "Est. retail value",
                      accessor: (l) =>
                        l.estimated_retail_value != null
                          ? `$${l.estimated_retail_value.toFixed(2)}`
                          : "—",
                    },
                  ]}
                />
              </div>
            </div>
          ))}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                className="btn-secondary text-xs py-1 px-3"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Newer
              </button>
              <span className="text-xs text-gray-400">
                Page {page + 1} of {totalPages}
              </span>
              <button
                className="btn-secondary text-xs py-1 px-3"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Older
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Edit Schedule Modal ──────────────────────────────────────────────────────

type SlotState = { start: string; end: string };
type DaySlots  = Record<DayOfWeek, SlotState[]>;

const DAYS: DayOfWeek[] = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];

function ScheduleModal({
  partnerId,
  addresses,
  existing,
  onClose,
  onSuccess,
}: {
  partnerId: number;
  addresses: EntityAddress[];
  existing:  Schedule | null;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const isEditing = !!existing;

  const [addressId, setAddressId] = useState(
    existing ? String(existing.entity_address_id)
             : addresses.length === 1 ? String(addresses[0].id) : ""
  );

  const initSlots = (day: DayOfWeek): SlotState[] => {
    const saved = existing?.schedule[day];
    if (!saved || saved.length === 0) return [];
    return saved.map((s) => ({ start: slotToInput(s.start), end: slotToInput(s.end) }));
  };

  const [slots, setSlots] = useState<DaySlots>({
    monday:    initSlots("monday"),
    tuesday:   initSlots("tuesday"),
    wednesday: initSlots("wednesday"),
    thursday:  initSlots("thursday"),
    friday:    initSlots("friday"),
    saturday:  initSlots("saturday"),
    sunday:    initSlots("sunday"),
  });

  const addSlot    = (day: DayOfWeek) =>
    setSlots((prev) => ({ ...prev, [day]: [...prev[day], { start: "", end: "" }] }));

  const removeSlot = (day: DayOfWeek, index: number) =>
    setSlots((prev) => ({ ...prev, [day]: prev[day].filter((_, i) => i !== index) }));

  const updateSlot = (day: DayOfWeek, index: number, field: "start" | "end", value: string) =>
    setSlots((prev) => {
      const updated = prev[day].map((s, i) => i === index ? { ...s, [field]: value } : s);
      return { ...prev, [day]: updated };
    });

  const buildPayload = () => {
    const payload: Record<string, Array<{ start: string; end: string }>> = {};
    for (const day of DAYS) {
      const complete = slots[day].filter((s) => s.start && s.end);
      if (complete.length > 0) {
        payload[day] = complete.map((s) => ({ start: inputToSlot(s.start), end: inputToSlot(s.end) }));
      }
    }
    return payload;
  };

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => {
      const schedule = buildPayload();
      if (isEditing) {
        return updateSchedule(existing.id, { entity_address_id: Number(addressId), schedule });
      }
      return createSchedule({ partner_entity_id: partnerId, entity_address_id: Number(addressId), schedule });
    },
    onSuccess,
  });

  const hasAtLeastOneSlot = DAYS.some((d) => slots[d].some((s) => s.start && s.end));
  const isValid = addressId && hasAtLeastOneSlot;

  return (
    <Modal title={isEditing ? "Edit schedule" : "Add schedule"} onClose={onClose}>
      <div className="space-y-4">
        {addresses.length > 1 && (
          <FormField label="Address">
            <Select value={addressId} onChange={(e) => setAddressId(e.target.value)}>
              <option value="" disabled>Select an address…</option>
              {addresses.map((a) => (
                <option key={a.id} value={a.id}>{a.address}, {a.city}</option>
              ))}
            </Select>
          </FormField>
        )}
        <p className="text-xs text-gray-500">Times in {SCHEDULE_TIME_ZONE_LABEL}</p>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {DAYS.map((day) => (
            <div key={day}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm capitalize text-gray-700 font-medium">{day}</span>
                <button
                  type="button"
                  className="text-xs text-brand-600 hover:text-brand-700 transition-colors"
                  onClick={() => addSlot(day)}
                >
                  + Add slot
                </button>
              </div>
              {slots[day].length === 0 ? (
                <p className="text-xs text-gray-400 pl-1">No pickups</p>
              ) : (
                <div className="space-y-1.5">
                  {slots[day].map((slot, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="time"
                        className="input flex-1"
                        value={slot.start}
                        onChange={(e) => updateSlot(day, i, "start", e.target.value)}
                      />
                      <span className="text-xs text-gray-400 shrink-0">to</span>
                      <input
                        type="time"
                        className="input flex-1"
                        value={slot.end}
                        onChange={(e) => updateSlot(day, i, "end", e.target.value)}
                      />
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-500 transition-colors text-sm shrink-0"
                        onClick={() => removeSlot(day, i)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {error && <p className="text-xs text-red-500">Failed to save. Please try again.</p>}

        <div className="flex gap-3 pt-2">
          <button className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary flex-1"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? "Saving…" : isEditing ? "Save schedule" : "Create schedule"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Add Blackout Day Modal ───────────────────────────────────────────────────

function AddBlackoutDayModal({
  partnerId,
  existingDates,
  onClose,
  onSuccess,
}: {
  partnerId:     number;
  existingDates: string[];
  onClose:       () => void;
  onSuccess:     () => void;
}) {
  const [date, setDate] = useState("");
  const isAlreadyBlackedOut = existingDates.includes(date);

  const { mutate, isPending } = useMutation({
    mutationFn: () => createBlackoutDay({ partner_entity_id: partnerId, date }),
    onSuccess,
  });

  return (
    <Modal title="Add blackout day" onClose={onClose}>
      <div className="space-y-4">
        <FormField
          label="Date"
          error={isAlreadyBlackedOut ? "This date is already a blackout day." : undefined}
        >
          <input
            className="input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </FormField>
        <div className="flex gap-3 pt-2">
          <button className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary flex-1"
            disabled={!date || isAlreadyBlackedOut || isPending}
            onClick={() => mutate()}
          >
            {isPending ? "Adding…" : "Add blackout day"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Contact user row ─────────────────────────────────────────────────────────

function ContactUserRow({ user, inactive }: { user: ContactUser; inactive?: boolean }) {
  const name    = `${user.name_1}${user.name_2 ? ` ${user.name_2}` : ""}`;
  const initials = `${user.name_1[0]}${user.name_2 ? user.name_2[0] : (user.name_1[1] ?? "")}`.toUpperCase();

  return (
    <div className={`flex items-center gap-3 ${inactive ? "opacity-50" : ""}`}>
      {/* Avatar */}
      {user.profile_photo_url ? (
        <img
          src={user.profile_photo_url}
          alt={name}
          className="w-10 h-10 rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold shrink-0">
          {initials}
        </div>
      )}

      {/* Details */}
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{name}</p>
        {user.title && (
          <p className="text-xs text-gray-500 truncate">{user.title}</p>
        )}
        {user.email && (
          <a
            href={`mailto:${user.email}`}
            className="text-xs text-brand-600 hover:text-brand-700 transition-colors truncate block"
            onClick={(e) => e.stopPropagation()}
          >
            {user.email}
          </a>
        )}
        {user.phone && (
          <p className="text-xs text-gray-400 truncate">{user.phone}</p>
        )}
      </div>
    </div>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <span className="text-sm text-gray-500 w-24 shrink-0">{label}</span>
      <span className="text-sm text-gray-900">{value}</span>
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title:    string;
  action?:  React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}
